<template>
  <WidgetBaseComponent
    v-resize="checkTruncation"
    ref="widget"
    class="next-events-widget"
    :loadedData="allDataLoaded && !showSpinner"
  >
    <!-- HEADER -->
    <template #header>
      <div class="header-bar">
        <h2>{{ expedientGlobalVisionAppointments.title }}</h2>
        <v-menu bottom left nudge-bottom="24" content-class="navigation-context-menu">
          <template #activator="{ on, attrs }">
            <button
              v-if="userHasPermissionToSaveAtLeastOneActionType"
              :class="[icons.addButton, 'add-button']"
              v-bind="attrs"
              v-on="on"
            ></button>
          </template>

          <v-list class="list">
            <v-list-item
              v-for="(item, index) in contextMenuItems"
              :key="index"
              class="list-item"
              @click="onContextMenuItemClicked(item.actionType)"
            >
              <span :class="item.iconCss"></span>
              <span>{{ item.text }}</span>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>

    <!-- BODY -->
    <template #body>
      <section v-if="expedientGlobalVisionAppointments.body.length > 0" class="events-container">
        <div
          v-for="(event, index) in expedientGlobalVisionAppointments.body"
          :key="index"
          class="event"
          @click.stop="goToEvent(event)"
        >
          <span :class="[checkActionIcon(event.idActionType), 'lf-icon__md', 'event-icon']"></span>
          <div class="event-info-container">
            <LexonTooltipComponent v-if="isTruncatedItems[index]" :message="event.subject" position="top" isWidthAuto>
              <template #activator-element>
                <span :ref="(el) => setRef(el, index)" class="event-subject">{{ event.subject }}</span>
              </template>
            </LexonTooltipComponent>
            <span v-else :ref="(el) => setRef(el, index)" class="event-subject">{{ event.subject }}</span>
            <div class="event-info">
              <div class="data">
                <span class="label">{{ text.date }}</span>
                <span>{{ event.startDate }}</span>
              </div>
              <div class="data">
                <span class="label">{{ text.hour }}</span>
                <span>{{ `${event.hourStart}h - ${event.hourEnd}h` }}</span>
              </div>
              <div class="data">
                <span class="label">{{ text.duration }}</span>
                <span>{{ event.duration || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div v-else class="no-events-container">
        <div class="wrapper">
          <span :class="[icons.archiveEmpty, 'explanation-icon', 'lf-icon__xl']"></span>
          <div class="explanation-container">
            <span class="explanation-title">{{ text.noEvents }}</span>
            <p class="explanation">{{ text.explanation }}</p>
          </div>
        </div>
      </div>
    </template>
  </WidgetBaseComponent>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import useRouter from '@/composables/useRouter'
import { useI18n } from 'vue-i18n-composable'
import useTruncation from '@/composables/useTruncation'
import WidgetBaseComponent from '@/components/widgets/WidgetBaseComponent.vue'
import { Icons } from '@/icons/icons'
import { ActionTypes, checkActionIcon } from '@/store/modules/actions/actionsTypes'
import { URLS } from '@/router/routes/urlRoutes'
import { ContextName } from '@/store/types/storeGlobalTypes'
import { TabNames } from '@/store/modules/tabs/tabsTypes'
import { entity } from '@/store/modules/entities/entitiesTypes'
import usePermissions from '@/composables/usePermissions'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useGlobalVisionWidgets from '@/components/expedients/ExpedientCourts/composables/useGlobalVisionWidgets'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'

// CONSTS
const contexts = {
  expedientSchedule: ContextName.EXPEDIENT_SCHEDULE_WIDGET,
  expedientContext: ContextName.EXPEDIENTS
}

const icons = {
  actionCall: Icons.CALL,
  actionMeeting: Icons.MEETING,
  actionOther: Icons.OTHERS_F,
  actionProcedure: Icons.PROCEDURES,
  addButton: Icons.ADD,
  archiveEmpty: Icons.ARCHIVE_EMPTY,
  internalManagement: Icons.INTERNAL_MANAGEMENT,
  task: Icons.TASK
}

// ROUTER
const router = useRouter()

// TRANSLATIONS
const { t } = useI18n()

const text = {
  noEvents: t('components.widgets.expedient_next_events.no_events'),
  explanation: t('components.widgets.expedient_next_events.explanation'),
  date: t('components.widgets.expedient_next_events.date'),
  hour: t('components.widgets.expedient_next_events.hour'),
  duration: t('components.widgets.expedient_next_events.duration')
}

// COMPOSABLES
const { checkEntityPermissionsGetter, userHasPermissionToSaveAtLeastOneActionType } = usePermissions()
const expedientRegister = useSelectedRegister({ entityName: contexts.expedientContext })
const { selectedRegisterId: selectedExpedientId } = expedientRegister
const { expedientStage } = useStage()
const { expedientGlobalVisionAppointments, fetchExpedientGlobalVisionAppointments, resetAppointements } =
  useGlobalVisionWidgets()

const { isTruncatedItems, checkTruncation, setRef } = useTruncation({
  items: computed(() => expedientGlobalVisionAppointments.value?.body || [])
})

// REFS
const widget = ref<any>(null)
const showSpinner = ref(true)

// COMPUTED
const expedientStageStateGetter = computed(() => expedientStage.value)

const allDataLoaded = computed(() => {
  return !!(expedientGlobalVisionAppointments.value && Object.keys(expedientGlobalVisionAppointments.value).length)
})

const contextMenuItems = computed(() => {
  return [
    ...(checkEntityPermissionsGetter(entity.actions.calls.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.calls'),
            iconCss: icons.actionCall,
            actionType: ActionTypes.CALLS
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.meetings.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.meetings'),
            iconCss: icons.actionMeeting,
            actionType: ActionTypes.MEETINGS
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.procedures.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.procedures'),
            iconCss: icons.actionProcedure,
            actionType: ActionTypes.PROCEDURES
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.task.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.task'),
            iconCss: icons.task,
            actionType: ActionTypes.TASK
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.internalManagement.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.internal_management'),
            iconCss: icons.internalManagement,
            actionType: ActionTypes.INTERNAL_MANAGEMENT
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.others.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.others_f'),
            iconCss: icons.actionOther,
            actionType: ActionTypes.OTHERS
          }
        ]
      : [])
  ]
})

// METHODS
const fetchEvents = async () => {
  await fetchExpedientGlobalVisionAppointments({
    idExpedient: selectedExpedientId.value,
    source: contexts.expedientSchedule,
    idStage: expedientStageStateGetter.value
  })
  showSpinner.value = false
}

const onContextMenuItemClicked = (actionType: string) => {
  router.goToRouteWithQuery({
    name: `${URLS.EXPEDIENTS}-${URLS.EXPEDIENTS}`,
    params: { selectedRegisterId: selectedExpedientId.value },
    query: {
      tab: TabNames.ACTIONS,
      action: actionType
    }
  })
}

const goToEvent = (event: any) => {
  const routeData = router.resolveQueryRoute({
    name: URLS.AGENDA,
    query: { id: event.id }
  })
  window.open(routeData?.href, '_blank')
}

// WATCHERS
watch(expedientStageStateGetter, () => {
  showSpinner.value = true
  fetchEvents()
})

// HOOKS
onMounted(() => {
  setTimeout(() => {
    fetchEvents()
  })
})

onBeforeUnmount(() => {
  resetAppointements()
})
</script>

<style lang="scss" scoped>
@import '~@/components/Navigation/AppBarToolbarItems/contextMenus/styles/contextmenu.scss';

.navigation-context-menu {
  --menu-with: 200px;
}

.next-events-widget {
  min-height: 207px;
  border-radius: $cornerRadius-md;
  border: 1px solid $main-300;

  .header-bar {
    @include flex($justify-content: space-between);
    width: 100%;

    .add-button {
      @include borders;
      display: flex;
      padding: $spacing-xxs;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: $cornerRadius-sm;
    }
  }

  .events-container {
    @include scroll-styles;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;
    padding-bottom: $spacing-xs;

    .event {
      display: flex;
      align-items: flex-start;
      align-self: stretch;
      border-radius: $cornerRadius-sm;
      border: 1px solid $neutral-grey-200;
      border-left: 2px solid $tertiary-marianBlue-100;
      width: 257px;
      min-width: 257px;
      padding: $spacing-xs;
      gap: $spacing-xs;
      cursor: pointer;

      .event-icon {
        display: flex;
        align-items: flex-start;
        color: $main-1000;
      }

      .event-info-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: $spacing-xs;
        flex: 1 0 0;
        overflow: hidden;

        .event-subject {
          @include milano-bold-14;
          @include ellipsis;
          display: inline-block;
          color: $main-1000;
          max-width: 100%;
        }

        .event-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          align-self: stretch;

          .data {
            @include milano-regular-14;
            color: $neutral-grey-800;

            .label {
              @include milano-bold-14;
              margin-right: $spacing-xs;
            }
          }
        }
      }
    }
  }

  .no-events-container {
    display: flex;
    padding: $spacing-lg $spacing-xl;
    justify-content: flex-start;
    align-items: center;
    gap: $spacing-xs;
    align-self: stretch;
    border-radius: $cornerRadius-sm;

    .wrapper {
      @include milano-medium-16;
      display: flex;
      align-items: center;
      color: $neutral-grey-600;

      .explanation-icon {
        align-self: flex-start;
        margin-right: $spacing-xs;
        margin-top: $spacing-xxs;
        color: $main-400;
      }

      .explanation {
        @include milano-regular-14;
        margin: 0;
      }
    }
  }
}
</style>
